import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Check, Sparkles, Crown, Rocket, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  starter_plan: Rocket,
  pro_plan: Sparkles,
  enterprise_plan: Crown,
};

const PLAN_FEATURES = {
  starter_plan: [
    "Up to 25 workers",
    "Attendance tracking",
    "Leave management",
    "Basic reports (CSV)",
    "Email support",
  ],
  pro_plan: [
    "Unlimited workers",
    "Everything in Starter",
    "PDF wage reports with branding",
    "WhatsApp payslips",
    "Priority email support",
    "Multi-user access",
  ],
  enterprise_plan: [
    "Everything in Pro",
    "Multi-farm support",
    "Dedicated account manager",
    "Custom integrations",
    "24/7 phone support",
    "SLA guarantee",
  ],
};

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState('month');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments/plans`),
        axios.get(`${API_URL}/api/payments/subscription`, { withCredentials: true }).catch(() => ({ data: { active: false } })),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async (lookupKey) => {
    setCheckoutLoading(lookupKey);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel subscription at the end of the current billing period?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/payments/cancel-subscription`, {}, { withCredentials: true });
      toast.success('Subscription will be cancelled at period end');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  const getYearlySavings = (prices) => {
    const monthly = prices.find(p => p.interval === 'month');
    const yearly = prices.find(p => p.interval === 'year');
    if (!monthly || !yearly) return 0;
    const savings = (monthly.amount * 12) - yearly.amount;
    return Math.round((savings / (monthly.amount * 12)) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pricing-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1
          className="text-3xl md:text-5xl font-bold text-[#1C1C1E] tracking-tight"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Choose Your Plan
        </h1>
        <p className="text-[#5C5C5C] mt-3 text-lg">
          Simple, transparent pricing that grows with your farm
        </p>
      </div>

      {/* Current Subscription Banner */}
      {subscription?.active && (
        <Card className="bg-[#2E4632] text-white rounded-lg shadow-sm max-w-3xl mx-auto" data-testid="current-subscription-banner">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-5 h-5" />
                  <span className="text-sm font-medium text-white/80 uppercase tracking-wide">Active Subscription</span>
                </div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {subscription.plan?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                {subscription.current_period_end && (
                  <p className="text-sm text-white/70 mt-1">
                    {subscription.cancel_at_period_end ? 'Ends' : 'Renews'} on{' '}
                    {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                  </p>
                )}
              </div>
              {!subscription.cancel_at_period_end && (
                <Button
                  variant="outline"
                  data-testid="cancel-subscription-btn"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="h-11 bg-white text-[#2E4632] hover:bg-white/90 border-white"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing interval toggle */}
      <div className="flex justify-center">
        <Tabs value={interval} onValueChange={setInterval}>
          <TabsList className="bg-[#E8E8E3] h-12" data-testid="interval-tabs">
            <TabsTrigger
              value="month"
              data-testid="interval-monthly"
              className="h-10 px-6 data-[state=active]:bg-[#2E4632] data-[state=active]:text-white font-semibold"
            >
              Monthly
            </TabsTrigger>
            <TabsTrigger
              value="year"
              data-testid="interval-yearly"
              className="h-10 px-6 data-[state=active]:bg-[#2E4632] data-[state=active]:text-white font-semibold"
            >
              Yearly
              <Badge className="ml-2 bg-[#D97706]/10 text-[#D97706] hover:bg-[#D97706]/10">
                Save 20%
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const price = plan.prices.find(p => p.interval === interval);
          if (!price) return null;
          const Icon = PLAN_ICONS[plan.id] || Rocket;
          const features = PLAN_FEATURES[plan.id] || [];
          const isPro = plan.id === 'pro_plan';
          const savingsPercent = interval === 'year' ? getYearlySavings(plan.prices) : 0;
          const isCurrentPlan = subscription?.active && subscription?.plan === price.lookup_key;
          const isCheckoutLoading = checkoutLoading === price.lookup_key;

          const getButtonClass = () => {
            if (isCurrentPlan) return 'bg-[#5C5C5C] text-white';
            if (isPro) return 'bg-[#D97706] hover:bg-[#B45309] text-white';
            return 'bg-[#2E4632] hover:bg-[#1F3122] text-white';
          };

          const getButtonLabel = () => {
            if (isCheckoutLoading) {
              return (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              );
            }
            if (isCurrentPlan) return 'Current Plan';
            return 'Subscribe Now';
          };

          return (
            <Card
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-lg shadow-sm card-hover ${
                isPro
                  ? 'bg-[#2E4632] text-white border-2 border-[#D97706]'
                  : 'bg-white border border-[#D1D1CD]'
              }`}
            >
              {isPro && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#D97706] text-white hover:bg-[#D97706] px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  isPro ? 'bg-[#D97706]' : 'bg-[#2E4632]'
                }`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3
                  className={`text-2xl font-bold ${isPro ? 'text-white' : 'text-[#1C1C1E]'}`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {plan.name}
                </h3>
                <p className={`text-sm mt-1 ${isPro ? 'text-white/80' : 'text-[#5C5C5C]'}`}>
                  {plan.description}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`text-4xl font-bold ${isPro ? 'text-white' : 'text-[#1C1C1E]'}`}
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {formatPrice(price.amount, price.currency)}
                    </span>
                    <span className={`text-sm ${isPro ? 'text-white/70' : 'text-[#5C5C5C]'}`}>
                      /{interval === 'month' ? 'mo' : 'yr'}
                    </span>
                  </div>
                  {savingsPercent > 0 && (
                    <p className={`text-xs mt-1 ${isPro ? 'text-[#D97706]' : 'text-[#D97706]'} font-medium`}>
                      Save {savingsPercent}% vs monthly
                    </p>
                  )}
                </div>

                <ul className="space-y-3">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        isPro ? 'text-[#D97706]' : 'text-[#2E4632]'
                      }`} />
                      <span className={`text-sm ${isPro ? 'text-white/90' : 'text-[#1C1C1E]'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(price.lookup_key)}
                  data-testid={`subscribe-${plan.id}`}
                  disabled={isCheckoutLoading || isCurrentPlan}
                  className={`w-full h-12 font-semibold ${getButtonClass()}`}
                >
                  {getButtonLabel()}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info footer */}
      <div className="max-w-3xl mx-auto">
        <Card className="bg-white border border-[#D1D1CD] rounded-lg">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#5C5C5C]">
              <p className="font-semibold text-[#1C1C1E] mb-1">Test mode</p>
              <p>
                Use test card <code className="bg-[#F4F4F0] px-2 py-0.5 rounded text-[#1C1C1E] font-mono text-xs">4242 4242 4242 4242</code> with any future expiry and any CVC. Tax is automatically calculated and remitted by Stripe.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Pricing;
